import { useEffect, useMemo, useState } from 'react';
import { onSnapshot } from 'firebase/firestore';
import type { TaskComment } from '../types';
import { architectDesignTaskCommentsQuery } from '../utils/architectDesignCommentQuery';
import {
  architectDesignCommentScopeKey,
  architectDesignCommentScopesFromProjects,
  type ArchitectDesignCommentScope,
} from '../utils/architectDesignCommentScopes';

export type { ArchitectDesignCommentScope };
export { architectDesignCommentScopesFromProjects };

function mapCommentDoc(id: string, data: Record<string, unknown>): TaskComment {
  const createdAt = data.createdAt;
  let created: Date;
  if (createdAt instanceof Date) created = createdAt;
  else if (createdAt && typeof (createdAt as { toDate?: () => Date }).toDate === 'function') {
    created = (createdAt as { toDate: () => Date }).toDate();
  } else {
    created = new Date((createdAt as string | number | undefined) || 0);
  }
  return {
    id,
    ...data,
    createdAt: created,
  } as TaskComment;
}

/**
 * Live Architect Discussion comments for Status / CRM hydration.
 * Same query Project Hub uses in decisionOnly mode — one listen per
 * projectId + dealId::design + audience=architect.
 */
export function useArchitectDesignComments(
  scopes: ArchitectDesignCommentScope[],
  enabled: boolean,
): TaskComment[] {
  const scopesKey = useMemo(() => {
    if (!enabled) return '';
    return scopes
      .map((s) => architectDesignCommentScopeKey(s))
      .filter(Boolean)
      .sort()
      .join('|');
  }, [enabled, scopes]);

  const [comments, setComments] = useState<TaskComment[]>([]);

  useEffect(() => {
    if (!enabled || !scopesKey) {
      setComments([]);
      return;
    }
    const activeScopes = scopesKey.split('|').map((key) => {
      const [projectId, dealId] = key.split('::');
      return { projectId, dealId };
    });
    const rowsBySource = new Map<string, TaskComment[]>();
    const flush = () => {
      const byId = new Map<string, TaskComment>();
      rowsBySource.forEach((rows) => {
        for (const row of rows) {
          if (row.id) byId.set(row.id, row);
        }
      });
      setComments(Array.from(byId.values()));
    };
    const unsubs = activeScopes.map((scope) => {
      const key = architectDesignCommentScopeKey(scope);
      return onSnapshot(
        architectDesignTaskCommentsQuery(scope.projectId, scope.dealId),
        (snapshot) => {
          rowsBySource.set(
            key,
            snapshot.docs.map((d) => mapCommentDoc(d.id, d.data() as Record<string, unknown>)),
          );
          flush();
        },
        (err) => {
          console.error(
            '[useArchitectDesignComments] listen failed',
            scope.projectId,
            scope.dealId,
            err,
          );
          rowsBySource.set(key, []);
          flush();
        },
      );
    });
    return () => unsubs.forEach((unsub) => unsub());
  }, [enabled, scopesKey]);

  return comments;
}
